import {
  useState, useEffect, useCallback, useRef, cloneElement, isValidElement,
} from "react";
import type { ReactElement, ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { SHOP_PAGE_CONFIG } from "@/features/shop";

export type CarouselSlide = {
  id: string
  content: ReactNode
};

export type Slide = CarouselSlide;
export type CarouselSliderProps = {
  slides: CarouselSlide[]
  autoplayInterval?: number
  className?: string
};

export const CarouselSlider = ({
  slides,
  autoplayInterval = SHOP_PAGE_CONFIG.BANNER_SLIDER_AUTOPLAY_INTERVAL,
  className = "",
}: CarouselSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isHoveredRef = useRef(false);

  const setHoverState = useCallback((hovered: boolean) => {
    isHoveredRef.current = hovered;
  }, []);

  const goToPrevious = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToNext = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
  }, [slides.length]);

  const handleMouseEnter = useCallback(() => {
    setHoverState(true);
  }, [setHoverState]);

  const handleMouseLeave = useCallback(() => {
    setHoverState(false);
  }, [setHoverState]);

  useEffect(() => {
    if (slides.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      if (!isHoveredRef.current) {
        goToNext();
      }
    }, autoplayInterval);

    return () => {
      clearInterval(interval);
    };
  }, [autoplayInterval, goToNext, slides.length]);

  if (slides.length === 0) {
    return null;
  }

  return (
    <div
      className={`relative overflow-hidden rounded-lg ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative h-full w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={slides[currentIndex].id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="h-full w-full"
          >
            {isValidElement(slides[currentIndex].content)
              && typeof slides[currentIndex].content.type !== "string"
              && typeof slides[currentIndex].content.type !== "symbol"
              ? cloneElement(
                slides[currentIndex].content as ReactElement<{ onHoverChange?: (hovered: boolean) => void }>,
                { onHoverChange: setHoverState },
              )
              : slides[currentIndex].content}
          </motion.div>
        </AnimatePresence>
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Slide précédent"
            onClick={goToPrevious}
            className="absolute left-1 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 sm:left-2 sm:h-10 sm:w-10"
          >
            <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
          </button>

          <button
            type="button"
            aria-label="Slide suivant"
            onClick={goToNext}
            className="absolute right-1 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 sm:right-2 sm:h-10 sm:w-10"
          >
            <ChevronRight size={20} className="sm:w-6 sm:h-6" />
          </button>
        </>
      )}

      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5 sm:gap-2">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`h-1.5 w-1.5 rounded-full transition-all duration-300 sm:h-2 sm:w-2 ${
                index === currentIndex ? "w-4 bg-white sm:w-6" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const BannerSlider = CarouselSlider;
